import React, { useEffect, useState } from 'react'
import { Form, Modal } from 'react-bootstrap'
import { Input } from 'iebt-zing-storybook'
import { trans } from 'sharedKernel/i18n'
import { EMAIL_REGEX } from 'sharedKernel/utils/validation'
import {
  CloseButton,
  ConfirmButton,
  FooterActions,
  FormFieldsContainer,
  StatusMessage,
  StyledModal,
} from './style'

interface CancelReservationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (data: { reservationCode: string; email: string }) => Promise<void>
}

export const CancelReservationModal = ({
  isOpen,
  onClose,
  onConfirm,
}: CancelReservationModalProps): JSX.Element => {
  const [reservationCode, setReservationCode] = useState('')
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  const labels = {
    title: trans('meetingRoom.public.cancelModal.title'),
    reservationCode: trans('meetingRoom.public.cancelModal.reservationCode'),
    reservationEmail: trans('meetingRoom.public.cancelModal.reservationEmail'),
    reservationCodePlaceholder: trans('meetingRoom.public.cancelModal.reservationCodePlaceholder'),
    reservationEmailPlaceholder: trans('meetingRoom.public.cancelModal.reservationEmailPlaceholder'),
    confirm: trans('meetingRoom.public.cancelModal.confirm'),
    close: trans('meetingRoom.public.cancelModal.close'),
    success: trans('meetingRoom.public.cancelModal.success'),
    error: trans('meetingRoom.public.cancelModal.error'),
  }

  useEffect(() => {
    if (!isOpen) {
      setReservationCode('')
      setEmail('')
      setIsSubmitting(false)
      setErrorMessage('')
      setSuccessMessage('')
    }
  }, [isOpen])

  const isFormValid =
    reservationCode.trim().length > 0 && EMAIL_REGEX.test(email.trim())

  const handleConfirm = async (): Promise<void> => {
    if (!isFormValid || isSubmitting) return

    setIsSubmitting(true)
    setErrorMessage('')
    setSuccessMessage('')

    try {
      await onConfirm({
        reservationCode: reservationCode.trim(),
        email: email.trim(),
      })
      setSuccessMessage(labels.success)
      setReservationCode('')
      setEmail('')
    } catch {
      setErrorMessage(labels.error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <StyledModal show={isOpen} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{labels.title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={e => e.preventDefault()}>
          <FormFieldsContainer>
            <Input
              id="cancel-reservation-code"
              showCounter={false}
              isRequired
              label={labels.reservationCode}
              value={reservationCode}
              onChange={event => {
                setReservationCode(event.target.value)
                setErrorMessage('')
                setSuccessMessage('')
              }}
              placeholder={labels.reservationCodePlaceholder}
            />

            <Input
              id="cancel-reservation-email"
              code="email"
              showCounter={false}
              isRequired
              label={labels.reservationEmail}
              value={email}
              onChange={event => {
                setEmail(event.target.value)
                setErrorMessage('')
                setSuccessMessage('')
              }}
              placeholder={labels.reservationEmailPlaceholder}
            />

            {errorMessage ? <StatusMessage $tone="error">{errorMessage}</StatusMessage> : null}
            {successMessage ? <StatusMessage $tone="success">{successMessage}</StatusMessage> : null}
          </FormFieldsContainer>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <FooterActions>
          <ConfirmButton
            type="button"
            disabled={!isFormValid || isSubmitting}
            onClick={() => { void handleConfirm() }}
          >
            {labels.confirm}
          </ConfirmButton>
          <CloseButton type="button" onClick={onClose} disabled={isSubmitting}>
            {labels.close}
          </CloseButton>
        </FooterActions>
      </Modal.Footer>
    </StyledModal>
  )
}
